import type { MouseEvent, ReactNode } from 'react';
import { useHref, useNavigate } from 'react-router-dom';
import './row-link.css';

/** What a click on these already does for itself; the row stays out of its way. */
const OWN_CLICK = 'a, button, input, select, textarea, label, summary';

/**
 * **A table row whose whole width opens one page.** The row's name stays a real
 * link (keyboard, a middle click, the address on hover); this makes a click
 * anywhere else on the row go where that link goes.
 *
 * Not a stretched `::after` on the link over a `position: relative` row:
 * Safari does not make a table row the box for an absolutely positioned
 * descendant, so every row's overlay grew to cover the whole table and the
 * last row took every click on the page (2026-10-02, Lew's Safari).
 */
export function useRowLink(to: string | null) {
  const navigate = useNavigate();
  const href = useHref(to ?? '.');
  if (to === null) return {};
  return {
    className: 'row-link',
    onClick(event: MouseEvent<HTMLTableRowElement>) {
      const action = rowClickAction({
        prevented: event.defaultPrevented,
        button: event.button,
        ownClick: (event.target as Element).closest(OWN_CLICK) !== null,
        selecting: Boolean(window.getSelection()?.toString()),
        newTab: event.metaKey || event.ctrlKey || event.shiftKey,
      });
      if (action === 'tab') window.open(href, '_blank', 'noopener');
      else if (action === 'go') navigate(to);
    },
  };
}

/**
 * What a click on a link row does: nothing where something on the row handles
 * its own click (a link, a button, a select) or the reader was selecting text;
 * a new tab with a modifier held; otherwise the row's page.
 */
export function rowClickAction(click: {
  prevented: boolean;
  button: number;
  ownClick: boolean;
  selecting: boolean;
  newTab: boolean;
}): 'none' | 'tab' | 'go' {
  if (click.prevented || click.button !== 0 || click.ownClick || click.selecting) return 'none';
  return click.newTab ? 'tab' : 'go';
}

/** A `<tr>` that opens `to` from anywhere on it; `null` is an ordinary row. */
export function LinkRow({ to, children }: { to: string | null; children: ReactNode }) {
  return <tr {...useRowLink(to)}>{children}</tr>;
}
